// FamilyChat.tsx - Family Chat Component (OpenClaw Social benzeri)
// Proje family chat sistemi - agent + user mesajlari

import axios from 'axios'
import { ChevronDown, MessageCircle, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import MessageCard from './MessageCard'
import MessageInput from './MessageInput'

interface Message {
  id: number
  type: 'family' | 'user'
  project_id: number
  message_text: string
  created_at: string

  // Family message fields
  agent_id?: number
  expert_id?: string
  cycle_number?: number
  message_type?: string

  // User message fields
  user_id?: number
  is_donor?: boolean

  // Reactions
  reactions?: Record<string, number>
}

interface FamilyChatProps {
  projectId: number
  currentUserId: number
  isDonor?: boolean
}

export default function FamilyChat({ projectId, currentUserId, isDonor = false }: FamilyChatProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'family' | 'user'>('all')
  const [showScrollButton, setShowScrollButton] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  // Scroll event listener
  useEffect(() => {
    const container = chatContainerRef.current
    if (!container) {
      return
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100
      setShowScrollButton(!isNearBottom)
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  const loadMessages = async () => {
    try {
      setLoading(true)

      const params: Record<string, string | number> = {
        limit: 50,
        offset: 0,
        user_id: currentUserId
      }

      if (filterType !== 'all') {
        params.message_type = filterType
      }

      const response = await axios.get<{ success: boolean; messages: Message[] }>(
        `${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}/family-chat`,
        { params }
      )

      if (response.data.success) {
        setMessages(response.data.messages)

        // Scroll to bottom after loading
        setTimeout(() => {
          scrollToBottom()
        }, 100)
      }
    } catch (error) {
      console.error('Error loading messages:', error)
    } finally {
      setLoading(false)
    }
  }

  // Mesajlari yukle
  useEffect(() => {
    void loadMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filterType])

  const handleSendMessage = async (messageText: string) => {
    try {
      setSending(true)

      const response = await axios.post<{ success: boolean }>(
        `${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}/family-chat`,
        {
          message_text: messageText,
          user_id: currentUserId,
          is_donor: isDonor
        }
      )

      if (response.data.success) {
        // Mesajlari yeniden yukle
        await loadMessages()
      }
    } catch (error: unknown) {
      console.error('Error sending message:', error)

      // Hata mesaji goster
      if (axios.isAxiosError(error) && error.response?.data?.detail) {
        console.warn(`Mesaj gonderilemedi: ${error.response.data.detail}`)
      }
    } finally {
      setSending(false)
    }
  }

  const handleReaction = async (messageId: number, messageType: 'family' | 'user', emoji: string) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}/family-chat/${messageId}/react?message_type=${messageType}`,
        {
          user_id: currentUserId,
          emoji: emoji
        }
      )

      if (response.data.success) {
        // Mesajlari yeniden yukle
        await loadMessages()
      }
    } catch (error) {
      console.error('Error adding reaction:', error)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      void loadMessages()
      return
    }

    try {
      setLoading(true)

      const response = await axios.get<{ success: boolean; messages: Message[] }>(
        `${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}/family-chat/search`,
        {
          params: {
            query: searchQuery,
            limit: 50,
            user_id: currentUserId
          }
        }
      )

      if (response.data.success) {
        setMessages(response.data.messages)
      }
    } catch (error) {
      console.error('Error searching messages:', error)
    } finally {
      setLoading(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--card-bg)',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--body-bg)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px'
        }}>
          <MessageCircle size={20} color="var(--primary-color)" />
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--text-primary)'
          }}>
            Family Chat
          </h3>
        </div>

        {/* Search + Filter */}
        <div style={{
          display: 'flex',
          gap: '8px'
        }}>
          {/* Search */}
          <div style={{
            flex: 1,
            display: 'flex',
            gap: '4px'
          }}>
            <input
              type="text"
              placeholder="Mesaj ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleSearch()
                }
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                background: 'var(--card-bg)',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            />
            <button
              onClick={() => {
                void handleSearch()
              }}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                background: 'var(--primary-color)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Search size={16} />
            </button>
          </div>

          {/* Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as 'all' | 'family' | 'user')}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">Tumu</option>
            <option value="family">Agentlar</option>
            <option value="user">Kullanicilar</option>
          </select>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: 'var(--text-secondary)'
          }}>
            Yukleniyor...
          </div>
        ) : messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: 'var(--text-secondary)'
          }}>
            Henuz mesaj yok. Ilk mesaji siz gonderin!
          </div>
        ) : (
          messages.map((message) => (
            <MessageCard
              key={`${message.type}-${message.id}`}
              message={message}
              currentUserId={currentUserId}
              onReaction={(messageId: number, messageType: 'family' | 'user', emoji: string) => {
                void handleReaction(messageId, messageType, emoji)
              }}
            />
          ))
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: '80px',
            right: '24px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            background: 'var(--primary-color)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 10
          }}
        >
          <ChevronDown size={20} />
        </button>
      )}

      {/* Input */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid var(--border-color)',
        background: 'var(--body-bg)'
      }}>
        <MessageInput
          onSend={(msg: string) => {
            void handleSendMessage(msg)
          }}
          disabled={sending}
          placeholder="Mesajinizi yazin..."
        />
      </div>
    </div>
  )
}
